/**
 * HelpArticlePage (/help/:id)
 * Article detail: title above the sanitized body, with a back link to /help.
 * Uses navigate(-1) (browser history back), the same mechanism
 * SupplierDetailPage uses to preserve the list's scroll position (AC 1056.9)
 * - relies on the browser's default scroll restoration rather than a custom
 *   mechanism.
 */
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileQuestion } from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import DetailPageHeader from '@components/common/DetailPageHeader';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import Button from '@components/common/Button';
import { helpService } from '@services';
import { ArticleBody } from '@components/features/help';
import styles from './HelpArticlePage.module.css';

const HelpArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const articleId = Number(id);

  const {
    data: article,
    isLoading,
    error
  } = useQuery({
    queryKey: ['help-article', articleId],
    queryFn: () => helpService.getArticle(articleId),
    enabled: Number.isFinite(articleId)
  });

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <PageLayout title="Help Article">
        <LoadingState message="Loading article..." spinnerSize="large" />
      </PageLayout>
    );
  }

  if (error || !article) {
    return (
      <PageLayout title="Help Article">
        <ErrorState
          icon={<FileQuestion size={48} />}
          title="Article Not Found"
          message="The help article you're looking for doesn't exist."
          action={
            <Button variant="primary" onClick={() => navigate('/help')}>
              Go to Help
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Help Article">
      <div className={styles.articlePage} data-test-id="help-article-page">
        <DetailPageHeader title={article.title} backLabel="Back to Help" onBack={handleBack} />

        <div className={styles.articleContent}>
          <ArticleBody html={article.body} />
        </div>
      </div>
    </PageLayout>
  );
};

export default HelpArticlePage;
